import React, { useState } from 'react';
import { ArrowLeft, Eye, EyeOff, Save } from 'lucide-react';

// Editor de Markdown
const MarkdownEditor = ({ document, onSave, onCancel, user }) => {
  const [formData, setFormData] = useState({
    title: document?.title || '',
    content: document?.content || '',
    category: document?.category || 'general',
    tags: document?.tags || [],
    description: document?.description || ''
  });
  const [isPreview, setIsPreview] = useState(false);

  const categories = [
    { id: 'general', name: 'General' },
    { id: 'api', name: 'APIs' },
    { id: 'database', name: 'Base de Datos' },
    { id: 'cloud', name: 'Cloud' },
    { id: 'security', name: 'Seguridad' },
    { id: 'performance', name: 'Performance' },
  ];

  const handleSave = () => {
    const docToSave = {
      ...formData,
      id: document?.id || 'doc_' + Date.now(),
      author: user.name,
      authorId: user.uid,
      lastUpdated: new Date().toISOString(),
      createdAt: document?.createdAt || new Date().toISOString()
    };
    onSave(docToSave);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900/95 backdrop-blur-xl rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col border border-white/20">
        {/* Header del editor */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center space-x-4">
            <button
              onClick={onCancel}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h2 className="text-xl font-semibold text-white">
              {document ? 'Editar Documento' : 'Nuevo Documento'}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsPreview(!isPreview)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              {isPreview ? <Eye className="w-5 h-5 text-white" /> : <EyeOff className="w-5 h-5 text-white" />}
            </button>
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all"
            >
              <Save className="w-4 h-4" />
              <span>Guardar</span>
            </button>
          </div>
        </div>

        {/* Formulario del editor */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Título del documento
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="Ej: Guía de Integración API REST"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Categoría
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id} className="bg-gray-800">
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tags (separados por coma)
                </label>
                <input
                  type="text"
                  value={formData.tags.join(', ')}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="react, typescript, api"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Descripción breve
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="Una breve descripción del documento"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Contenido (Markdown)
              </label>
              {isPreview ? (
                <div className="prose prose-invert max-w-none p-4 bg-white/5 border border-white/10 rounded-lg min-h-[300px]">
                  <div className="text-gray-300 whitespace-pre-wrap">
                    {formData.content || 'Nada que previsualizar aún...'}
                  </div>
                </div>
              ) : (
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono text-sm"
                  rows="15"
                  placeholder={`# Título del documento

## Sección 1
Contenido en **markdown**...

### Subsección
- Punto 1
- Punto 2

\`\`\`javascript
// Código de ejemplo
const example = "Hello World";
\`\`\`
`}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkdownEditor;
